import { Text, Tooltip } from '@mantine/core';
import { PriorityIcon } from 'components/PriorityIcon';
import { useTranslation } from 'react-i18next';
import { Column } from 'utils/models';
import { RuleItem, RuleStatuses, RuleTransitions, RuleType, RuleTypes } from 'models/rules';
import { BsCaretRightFill, BsCheck2, BsPeople, BsPerson, BsSearch, BsTextRight, BsXCircle } from 'react-icons/bs';
import { useChangeStatusRule } from 'hooks/api/rule/useChangeStatusRule';
import { useState } from 'react';
import { useExcludesRule } from 'hooks/api/rule/useExcludesRule';
import { useParams } from 'react-router-dom';
import { useDrawerContext } from 'context/hooks';
import { RuleDrawer } from 'components/Drawer/RuleDrawer/RuleDrawer';
import { TableColumnActions } from '../TableBody/TableColumnActions';
import { useActions } from '../Actions/RulesListActions';
import { TableColumnWithIcon } from '../TableBody/TableColumnWithIcon';
import { TableSwitch } from '../TableBody/TableSwitch';

interface Props {
  showTypeColumn?: boolean;
}

const iconSize = 16;

export const RulesColumns = ({ showTypeColumn = true }: Props): Column<RuleItem>[] => {
  const { t } = useTranslation();
  const actions = useActions();
  const { openDrawer } = useDrawerContext();
  const { mutate: changeStatusRule, isPending } = useChangeStatusRule({});
  const { patientId } = useParams<{ patientId: string }>();
  const { mutate: excludesRule, isPending: isPendingExcludes } = useExcludesRule();
  const [updatedItem, setUpdatedItem] = useState('');

  const handleSwitch = (item: RuleItem) => {
    setUpdatedItem(item.id);
    const newStatus = item.status === RuleStatuses.enabled ? RuleTransitions.disable : RuleTransitions.enable;
    changeStatusRule({ item, transition: newStatus });
  };

  const handleExclude = (item: RuleItem) => {
    setUpdatedItem(item.id);
    excludesRule({
      item,
      transition: item.excluded ? RuleTransitions.enable : RuleTransitions.disable,
      patientId,
    });
  };

  const renderTypeIcon = (type: RuleType) => {
    return type === RuleTypes.group ? <BsPeople size={iconSize} /> : <BsPerson size={iconSize} />;
  };

  const renderStatus = ({ status, type, excluded }: RuleItem) => {
    let color = 'var(--mantine-color-turquoise-5)';
    let icon = <BsCaretRightFill size={iconSize} />;
    let label = t(`rule_status_${status}`);

    if (status === RuleStatuses.draft) {
      color = '';
      icon = <BsTextRight size={iconSize} />;
    }
    if (status === RuleStatuses.inReview) {
      color = 'var(--mantine-color-blue-5)';
      icon = <BsSearch size={iconSize} />;
    }

    if (status === RuleStatuses.approved) {
      icon = <BsCheck2 size={iconSize} />;
    }

    if (type === RuleTypes.group && excluded) {
      color = 'var(--mantine-color-red-5)';
      label = t('excluded_label');
      icon = <BsXCircle size={iconSize} />;
    }

    return <TableColumnWithIcon icon={icon} label={label} color={color} />;
  };

  const renderEnabledAction = (item: RuleItem) => {
    const disabled = item.status === RuleStatuses.draft || item.status === RuleStatuses.inReview;
    if (item.type === RuleTypes.group) {
      return (
        <TableSwitch
          checked={!item.excluded}
          onChange={() => handleExclude(item)}
          isLoading={isPendingExcludes && item.id === updatedItem}
        />
      );
    }
    return (
      <Tooltip label={t('rule_disabled_tooltip')} disabled={!disabled} refProp={'rootRef'}>
        <TableSwitch
          checked={item.status === RuleStatuses.enabled}
          onChange={() => handleSwitch(item)}
          isLoading={isPending && item.id === updatedItem}
          disabled={disabled}
        />
      </Tooltip>
    );
  };

  const columns: Column<RuleItem>[] = [
    {
      header: t('label_rule'),
      width: 10,
      renderItem: (item) => <Text>{`${item.notification.title}`}</Text>,
      hideOnClick: (item) => item.type === RuleTypes.group,
      onClick: (item) =>
        openDrawer({
          content: <RuleDrawer />,
          size: '70%',
          title: t('edit_rule_label'),
          detailId: item.id,
        }),
    },
  ];

  if (showTypeColumn) {
    columns.push({
      header: t('type_label'),
      width: 10,
      renderItem: (item) =>
        item.type && <TableColumnWithIcon icon={renderTypeIcon(item.type)} label={t(`${item.type}_label`)} />,
    });
  }

  columns.push(
    {
      header: t('priority_label'),
      width: 10,
      renderItem: (item) => <PriorityIcon priority={Number(item.notification.priority)} />,
    },
    {
      header: t('description_label'),
      width: 20,
      renderItem: (item) => <Text>{`${item.notification.description}`}</Text>,
    },
    {
      header: t('status_table_heading'),
      width: 10,
      renderItem: (item) => renderStatus(item),
    },
    {
      header: t('table_actions'),
      width: 10,
      renderItem: (item) => <TableColumnActions<RuleItem> actions={actions} item={item} />,
    },
    {
      header: t('enabled_label'),
      width: 5,
      renderItem: (item) => renderEnabledAction(item),
    },
  );

  return columns;
};
